import * as React from "react";

import { IUnitRegistry } from "../Navigation/UnitRegistry/IUnitRegistry";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    emptyAbsoluteModelPath,
} from "../../../../../Common/ModelPath/ModelPath";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { ClearPageMethod } from "../../../Engine/ActionCreators/UnitActionCreators";
import {
    ContextualActionSet,
    ContextualActionsProvider,
} from "../../Internals/FormActionHandlers/ContextualActionsContext";

import { ModalMultipleUnitInstanceContainer } from "./Internals/ModalMultipleUnitInstance/ModalMultipleUnitInstanceContainer";
import { UnitLayoutingContext, UnitLayoutingContextProvider } from "./Internals/UnitBorderContext/UnitLayoutingContext";
import { UnitId, unitIdToLegacyUnitId } from "./UnitId/UnitId";
import { UnitIdProvider } from "./UnitIdContext";
import { UnitLayoutKind } from "./UnitLayoutKind";
import { MultipleUnitsContainer } from "./MultipleUnits/MultipleUnitsContainer";
import { SingleUnitContainer } from "./SingleUnitContainer";
import { ExtraPageAction, PropsPageActions } from "./Types";

export interface ChildPage {
    name?: string;
}

export interface UnitProps {
    noContent: boolean;
    unitId: UnitId;
    name?: string;
    bindingPath?: AbsoluteResolvedModelPath;
    unitRegistry: IUnitRegistry;
    children?: React.ReactNode;
    layoutKind: UnitLayoutKind;
    height?: number;
    hidden?: boolean;
    minHeight?: number;
    maxWidth?: number;
    multiple: boolean;
    optional?: boolean;
    hasDefaultContent?: boolean;
    pageActions: PropsPageActions;
    hideEnableAutoValues?: boolean;
    enableHorizontalScrolling?: boolean;
    pages: ChildPage[];
    clearConfirmationSettings: UnitClearConfirmationSettings;
    onHighlightUnit: (unitId: UnitId) => void;
    unitsWidth: { [key: string]: { width: number } };
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => void;
    onAddPage: () => void;
    onRemovePage: () => void;
    onClearPage: (method: ClearPageMethod) => void;
    onEnableAutoValues?: () => void;
    onMount: () => void;
    onUnmount: () => void;
    onRemoveAttachment?: () => void;
    onClearAttachment?: () => void;
    enableUploadFilesInPage: boolean;
    uploadFilesInPage: () => void;
    modalUnitId: undefined | UnitId;
    modalUnitTargetInstance?: string;
    showUnitBorder: boolean;
    isReadOnly?: boolean;
    fufSizeTooLargeErrorText?: string | React.ReactNode;
    unitsCountForPaging?: number;
    maxOccurs?: number;
    onOpenModalRequest?: (bindingPath: AbsoluteModelPath) => void;
    onCloseModalRequest?: (bindingPath: AbsoluteModelPath) => void;
    attachmentPageActions?: ExtraPageAction[];
}

export interface UnitClearConfirmationSettings {
    removeChildrenText?: string;
    keepChildrenCustomText?: string;
    clearChildrenCustomText?: string;
}

const regularUnitLayoutingContext: UnitLayoutingContext = {
    paddingRight: 40,
    paddingLeft: 40,
    marginTop: 40,
};

const regularUnitLayoutingContextWithoutBorder: UnitLayoutingContext = {
    paddingRight: 0,
    paddingLeft: 0,
    marginTop: 40,
};

export function Unit(props: UnitProps): JSX.Element {
    const unitLayoutingContext = props.showUnitBorder
        ? regularUnitLayoutingContext
        : regularUnitLayoutingContextWithoutBorder;

    React.useEffect(() => {
        LayoutEvents.emit();
    }, [props.modalUnitId]);

    function renderUnitContent(): JSX.Element {
        if (props.hidden) {
            return <></>;
        } else if (props.multiple) {
            return renderMultiple();
        } else if (props.modalUnitId) {
            return renderModalUnitInstance(props.modalUnitId);
        } else {
            return renderSingle();
        }
    }

    function renderModalUnitInstance(modalUnitId: UnitId): JSX.Element {
        return (
            <UnitIdProvider id={modalUnitId}>
                <ModalMultipleUnitInstanceContainer
                    key={unitIdToLegacyUnitId(modalUnitId)}
                    unitId={modalUnitId}
                    name={props.name}
                    pages={props.pages}
                    clearConfirmationSettings={props.clearConfirmationSettings}
                    showUnitBorder={props.showUnitBorder}
                    unitRegistry={props.unitRegistry}
                />
            </UnitIdProvider>
        );
    }

    function renderSingle(): JSX.Element {
        return (
            <SingleUnitContainer
                noContent={props.noContent}
                unitId={props.unitId}
                name={props.name}
                bindingPath={props.bindingPath}
                unitRegistry={props.unitRegistry}
                layoutKind={props.layoutKind}
                height={props.height}
                hidden={props.hidden}
                minHeight={props.minHeight}
                maxWidth={props.maxWidth}
                optional={props.optional}
                pages={props.pages}
                clearConfirmationSettings={props.clearConfirmationSettings}
                pageActions={props.pageActions ?? {}}
                hideEnableAutoValues={props.hideEnableAutoValues}
                enableHorizontalScrolling={props.enableHorizontalScrolling}
                onHighlightUnit={props.onHighlightUnit}
                unitsWidth={props.unitsWidth}
                updateEmptyUnits={props.updateEmptyUnits}
                onAddPage={props.onAddPage}
                onRemovePage={props.onRemovePage}
                onClearPage={onClearPage}
                isReadOnly={props.isReadOnly}
                onMount={props.onMount}
                onUnmount={props.onUnmount}
                uploadFilesInPage={props.uploadFilesInPage}
                enableUploadFilesInPage={props.enableUploadFilesInPage}
                onEnableAutoValues={props.onEnableAutoValues}
                onRemoveAttachment={props.onRemoveAttachment}
                showUnitBorder={props.showUnitBorder}
                extraPageActions={props.attachmentPageActions}
                hasAttachmentActions={props.attachmentPageActions && props.attachmentPageActions.length > 0}>
                {props.children}
            </SingleUnitContainer>
        );
    }

    function renderMultiple(): JSX.Element {
        return (
            <MultipleUnitsContainer
                updateEmptyUnits={props.updateEmptyUnits}
                unitId={props.unitId}
                name={props.name}
                unitsWidth={props.unitsWidth}
                height={props.height}
                hidden={props.hidden}
                minHeight={props.minHeight}
                enableHorizontalScrolling={props.enableHorizontalScrolling}
                bindingPath={props.bindingPath}
                layoutKind={props.layoutKind}
                pageActions={props.pageActions ?? {}}
                pages={props.pages}
                clearConfirmationSettings={props.clearConfirmationSettings}
                hideEnableAutoValues={props.hideEnableAutoValues}
                optional={props.optional}
                hasDefaultContent={props.hasDefaultContent ?? false}
                modalUnitId={props.modalUnitId}
                modalUnitTargetInstance={props.modalUnitTargetInstance}
                onHighlightUnit={props.onHighlightUnit}
                showUnitBorder={props.showUnitBorder}
                unitRegistry={props.unitRegistry}
                isReadOnly={props.isReadOnly}
                noContent={props.noContent}
                maxOccurs={props.maxOccurs}
                onMount={props.onMount}
                onUnmount={props.onUnmount}
                unitsCountForPaging={props.unitsCountForPaging}
                extraPageActions={props.attachmentPageActions}>
                {props.children}
            </MultipleUnitsContainer>
        );
    }

    const contextualActions: ContextualActionSet = React.useMemo(
        () => ({
            "modalInstanceUnit.Open": async params =>
                props.onOpenModalRequest?.(params.bindingPath ?? emptyAbsoluteModelPath()),
            "modalInstanceUnit.Close": async params =>
                props.onCloseModalRequest?.(params.bindingPath ?? emptyAbsoluteModelPath()),
        }),
        [props.onOpenModalRequest, props.onCloseModalRequest]
    );

    const onClearPage = (method: ClearPageMethod) => {
        if (props.onClearAttachment) {
            props.onClearAttachment();

            return;
        }

        props.onClearPage(method);
    };

    return (
        <ContextualActionsProvider actions={contextualActions}>
            <UnitLayoutingContextProvider value={unitLayoutingContext}>
                {renderUnitContent()}
            </UnitLayoutingContextProvider>
        </ContextualActionsProvider>
    );
}
